/**
 * TreeViewPage — Wave B
 *
 * Strategic Tree view at /roros/artifacts/tree.
 * Displays the full Vision → Goal/Outcome alternating tree.
 * Reuses the existing TreeView component from src/components/ui/TreeView/.
 *
 * Node visuals:
 *   Vision   — root, non-clickable large heading
 *   Goal     — target icon (Target), click opens GoalFormDialog in edit mode
 *   Outcome  — check-circle icon (CheckCircle2), click opens OutcomeFormDialog in edit mode
 *   Work Item — dot icon (Dot), click navigates to /roros/control-tower/:id
 *
 * Unanchored section: collapsed by default, lists orphan goals + outcomes with
 * a yellow warning: "Not connected to any superior entity."
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Target, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, GitBranch } from 'lucide-react';
import { RorosPage } from '@/components/roros/RorosPage';
import { LoadingState } from '@/components/ui/LoadingState';
import { TreeView } from '@/components/ui/TreeView/TreeView';
import type { TreeNode as UITreeNode } from '@/components/ui/TreeView/TreeView';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import { useRorosTree } from '@/hooks/roros/tree';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosGoal, useRorosGoalUpdate } from '@/hooks/roros/goals';
import { useRorosOutcome, useRorosOutcomeUpdate } from '@/hooks/roros/artifacts';
import type { TreeNode, TreeResponse } from '@/types/roros/tree';
import type { RorosGoalDTO, UpdateGoalPayload } from '@/types/roros/goals';
import type { RorosOutcomeDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { GoalFormDialog } from './GoalFormDialog';
import { OutcomeFormDialog } from './OutcomeFormDialog';
// H4: tree utilities extracted to tree.utils.ts
import { toUINode, findNodeById } from './tree.utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusVariant(status?: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'achieved': return 'success';
    case 'missed': return 'error';
    case 'archived': return 'default';
    default: return 'warning';
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function TreeViewPage() {
  const navigate = useNavigate();

  // ─── State ───────────────────────────────────────────────────────────────
  const [includeWorkItems, setIncludeWorkItems] = useState(false);
  const [showUnanchored, setShowUnanchored] = useState(false);

  // Edit dialogs state — Sven dogfood 2026-05-03: changed from holding a
  // partial DTO built from the TreeNode to holding just an id + the
  // domain id (for outcomes; goal hook only needs id). The full DTO is
  // fetched via useRorosGoal/useRorosOutcome below so the dialog gets
  // description, parent_type, parent_outcome_id, etc. — fields the
  // TreeNode shape doesn't carry.
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [editingOutcomeRef, setEditingOutcomeRef] = useState<{ id: string; domainId: string } | null>(null);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const treeQuery = useRorosTree({ includeWorkItems });
  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive);

  // Build domainId → name lookup for tree node labels
  const domainsById = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const d of domains) {
      if (d.id && d.name) map.set(d.id, d.name);
    }
    return map;
  }, [domains]);

  // Find strategy domain (domain_id = int 1 by convention, match first Strategy domain)
  const strategyDomain = domains.find((d: RorosDomainDTO) =>
    d.name?.toLowerCase() === 'strategy',
  );

  const treeData = treeQuery.data as TreeResponse | undefined;

  // Lazy-fetch full DTOs when an edit dialog is open. Hooks short-circuit
  // when the id is empty (enabled: !!id), so they're free when no dialog
  // is open. React Query caches against the same keys the dedicated pages
  // use, so navigating Goals page → Tree → click goal hits the cache.
  const editingGoalQuery = useRorosGoal(editingGoalId ?? '');
  const editingOutcomeQuery = useRorosOutcome(
    editingOutcomeRef?.domainId ?? '',
    editingOutcomeRef?.id ?? '',
  );
  const editingGoal: RorosGoalDTO | null = (editingGoalQuery.data as RorosGoalDTO | undefined) ?? null;
  const editingOutcome: RorosOutcomeDTO | null = (editingOutcomeQuery.data as RorosOutcomeDTO | undefined) ?? null;
  const isGoalDialogOpen = editingGoalId !== null;
  const isOutcomeDialogOpen = editingOutcomeRef !== null;

  // ─── Mutations ────────────────────────────────────────────────────────────
  const goalUpdateMutation = useRorosGoalUpdate();
  const outcomeUpdateMutation = useRorosOutcomeUpdate();

  // ─── Node click handlers ──────────────────────────────────────────────────

  // Click → set id-only state. The full DTO is fetched via the lazy hooks
  // declared above. Sven dogfood 2026-05-03: building a partial DTO from the
  // TreeNode dropped description + parent_type + parent_outcome_id, so the
  // dialog rendered with empty description and "None" anchor pill — even
  // though the data was fully present in the DB.
  const handleGoalClick = useCallback((goalId: string) => {
    setEditingGoalId(goalId);
  }, []);

  const handleOutcomeClick = useCallback((outcomeId: string) => {
    if (!treeData) return;
    // Need domainId to call the per-domain outcome detail endpoint.
    const outcomeNode = findNodeById(treeData.root, outcomeId)
      ?? treeData.unanchored.outcomes.find((n) => n.id === outcomeId);
    if (!outcomeNode || !outcomeNode.domainId) return;
    setEditingOutcomeRef({ id: outcomeId, domainId: outcomeNode.domainId });
  }, [treeData]);

  const handleWorkItemClick = useCallback((workItemId: string) => {
    navigate(`/roros/control-tower/${workItemId}`);
  }, [navigate]);

  // ─── Build UI tree nodes ───────────────────────────────────────────────────

  const uiTreeNodes = useMemo<UITreeNode[]>(() => {
    if (!treeData) return [];
    const rootNode = treeData.root;

    if (rootNode.type === 'vision') {
      // Vision root as a non-clickable heading; children become the tree nodes
      const visionUINode: UITreeNode = {
        id: 'vision-singleton',
        label: rootNode.title || t('Vision'),
        icon: undefined,
        disabled: false, // vision is "non-clickable" (selectionMode=none handles this)
        data: { type: 'vision' },
        children: rootNode.children.map((child) =>
          toUINode(child, handleGoalClick, handleOutcomeClick, handleWorkItemClick, domainsById),
        ),
      };
      return [visionUINode];
    }

    return rootNode.children.map((child) =>
      toUINode(child, handleGoalClick, handleOutcomeClick, handleWorkItemClick, domainsById),
    );
  }, [treeData, handleGoalClick, handleOutcomeClick, handleWorkItemClick, domainsById]);

  // ─── Handle tree node selection (clicking an item) ────────────────────────

  const handleSelect = useCallback(
    (_selectedIds: Set<string>, node: UITreeNode) => {
      const data = node.data as { type: string; nodeData?: TreeNode } | undefined;
      if (!data) return;

      if (data.type === 'goal') {
        handleGoalClick(node.id);
      } else if (data.type === 'outcome') {
        handleOutcomeClick(node.id);
      } else if (data.type === 'work_item') {
        handleWorkItemClick(node.id);
      }
    },
    [handleGoalClick, handleOutcomeClick, handleWorkItemClick],
  );

  // ─── Form submit handlers ─────────────────────────────────────────────────

  const handleGoalSubmit = useCallback(
    (payload: UpdateGoalPayload) => {
      if (!editingGoal) return;
      goalUpdateMutation.mutate(
        { id: editingGoal.id, payload },
        {
          onSuccess: () => {
            showToast.success(t('Goal updated'));
            setEditingGoalId(null);
            treeQuery.refetch();
          },
          onError: (err) => showToast.error(err.message),
        },
      );
    },
    [editingGoal, goalUpdateMutation, treeQuery],
  );

  const handleOutcomeSubmit = useCallback(
    (data: Record<string, unknown>) => {
      if (!editingOutcome) return;
      const { domainId: formDomainId, ...payload } = data;  // A1: form now sends domainId
      const domainId = (formDomainId as string) || editingOutcome.domainId;
      if (!domainId) {
        showToast.error(t('Failed to load strategic tree'));
        return;
      }
      outcomeUpdateMutation.mutate(
        { domainId, id: editingOutcome.id, payload: payload as any },
        {
          onSuccess: () => {
            showToast.success(t('Outcome updated'));
            setEditingOutcomeRef(null);
            treeQuery.refetch();
          },
          onError: (err) => showToast.error(err.message),
        },
      );
    },
    [editingOutcome, outcomeUpdateMutation, treeQuery],
  );

  // ─── Counts chip text ─────────────────────────────────────────────────────

  const countChips = useMemo(() => {
    if (!treeData) return null;
    const { goals, outcomes, orphanGoals, orphanOutcomes } = treeData.counts;
    const orphans = orphanGoals + orphanOutcomes;
    return (
      <span className="text-sm text-muted-foreground">
        {goals} {t('goals')} · {outcomes} {t('outcomes')}
        {orphans > 0 && (
          <span className="ml-1 text-amber-600">· {orphans} {t('orphans')}</span>
        )}
      </span>
    );
  }, [treeData]);

  // ─── Empty state ──────────────────────────────────────────────────────────

  const isEmpty = !treeData
    || (treeData.counts.goals === 0 && treeData.counts.outcomes === 0);

  // ─── Loading / error states ───────────────────────────────────────────────

  if (treeQuery.isError) {
    return (
      <RorosPage
        title={t('Strategic Tree')}
        breadcrumbs={[{ label: t('Strategic Tree'), path: '/roros/artifacts/tree' }]}
      >
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">{t('Failed to load strategic tree')}</p>
          <Button variant="outline" size="sm" onClick={() => treeQuery.refetch()}>
            {t('Retry')}
          </Button>
        </div>
      </RorosPage>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <RorosPage
      title={t('Strategic Tree')}
      breadcrumbs={[{ label: t('Strategic Tree'), path: '/roros/artifacts/tree' }]}
      actions={
        <div className="flex items-center gap-3">
          {countChips}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIncludeWorkItems((v) => !v)}
            data-testid="toggle-work-items"
            aria-pressed={includeWorkItems}
          >
            {includeWorkItems ? t('Hide work items') : t('Show work items')}
          </Button>
        </div>
      }
    >
      {treeQuery.isLoading ? (
        <LoadingState />
      ) : isEmpty ? (
        /* ─── Empty state ───────────────────────────────────────────────── */
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <GitBranch className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('No strategic content yet.')}</p>
          <p className="text-xs text-muted-foreground">
            {t('Start with a Vision Goal: create your first Outcome anchored to Vision')}
          </p>
          <Link
            to="/roros/artifacts/outcomes"
            className="text-sm font-medium text-primary hover:underline"
          >
            {t('Create your first Outcome →')}
          </Link>
        </div>
      ) : (
        /* ─── Tree ──────────────────────────────────────────────────────── */
        <div className="space-y-6">
          {/* Vision heading row */}
          {treeData && (
            <div className="mb-2">
              <h2 className="text-base font-semibold text-foreground">
                {treeData.root.title || t('Vision')}
              </h2>
            </div>
          )}

          {/* Main tree.
              chevronOnlyToggle: row click opens detail dialog (handleSelect);
              chevron click toggles expand/collapse without opening dialog.
              Sven dogfood 2026-05-03: pre-fix, both fired together. */}
          <TreeView
            nodes={uiTreeNodes}
            variant="bordered"
            size="md"
            showDefaultIcons={false}
            selectionMode="single"
            onSelect={handleSelect}
            defaultExpandAll
            chevronOnlyToggle
            data-testid="strategic-tree"
          />

          {/* ─── Unanchored section ──────────────────────────────────────── */}
          {treeData && (treeData.unanchored.goals.length > 0 || treeData.unanchored.outcomes.length > 0) && (
            <div className="mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowUnanchored((v) => !v)}
                aria-expanded={showUnanchored}
                data-testid="unanchored-toggle"
                className="flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800 transition-colors h-auto p-0"
              >
                {showUnanchored ? (
                  <ChevronDown className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                )}
                {t('Unanchored items')}
                <Badge variant="warning" className="text-xs">
                  {treeData.unanchored.goals.length + treeData.unanchored.outcomes.length}
                </Badge>
              </Button>

              {showUnanchored && (
                <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-4">
                  {/* Warning banner */}
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
                    <p className="text-xs text-amber-800">
                      {t('Not connected to any superior entity. Anchor it to give it strategic context.')}
                    </p>
                  </div>

                  {/* Orphan goals */}
                  {treeData.unanchored.goals.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700 mb-2">{t('Goals')}</p>
                      <div className="space-y-2">
                        {treeData.unanchored.goals.map((node) => (
                          <OrphanCard
                            key={node.id}
                            node={node}
                            onClick={() => handleGoalClick(node.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Orphan outcomes */}
                  {treeData.unanchored.outcomes.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700 mb-2">{t('Outcomes')}</p>
                      <div className="space-y-2">
                        {treeData.unanchored.outcomes.map((node) => (
                          <OrphanCard
                            key={node.id}
                            node={node}
                            // B6: show archived-parent hint if applicable
                            archivedParentLabel={
                              node.parentArchived && node.parentArchivedGoalTitle
                                ? `${t('Goal (archived):')} ${node.parentArchivedGoalTitle}`
                                : undefined
                            }
                            onClick={() => handleOutcomeClick(node.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Edit dialogs ─────────────────────────────────────────────────────
          Render conditions split by intent:
          - Click set the id → mount the dialog so it shows a loading state
            (or the cached DTO if React Query had it warm)
          - Lazy hooks fetch the full DTO with description + parent_type +
            parent_outcome_id — fields the TreeNode shape doesn't carry
          - Once data lands, the dialog populates correctly
       */}
      {editingGoalId && editingGoal && (
        <GoalFormDialog
          isOpen={isGoalDialogOpen}
          onClose={() => setEditingGoalId(null)}
          onSubmit={handleGoalSubmit}
          editingGoal={editingGoal}
          domains={domains}
          strategyDomainId={strategyDomain?.id ?? (domains[0]?.id ?? '')}
          isLoading={goalUpdateMutation.isPending || editingGoalQuery.isLoading}
        />
      )}

      {editingOutcomeRef && editingOutcome && (
        <OutcomeFormDialog
          isOpen={isOutcomeDialogOpen}
          onClose={() => setEditingOutcomeRef(null)}
          onSubmit={handleOutcomeSubmit}
          editingOutcome={editingOutcome}
          domains={domains}
          strategyDomainId={strategyDomain?.id ?? (domains[0]?.id ?? '')}
          isLoading={outcomeUpdateMutation.isPending || editingOutcomeQuery.isLoading}
        />
      )}
    </RorosPage>
  );
}

// ─── OrphanCard ───────────────────────────────────────────────────────────────

interface OrphanCardProps {
  node: TreeNode;
  onClick: () => void;
  // B6: optional archived-parent label for outcomes whose parent goal is archived
  archivedParentLabel?: string;
}

function OrphanCard({ node, onClick, archivedParentLabel }: OrphanCardProps) {
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full text-left p-2 rounded-md bg-white border border-amber-200 hover:border-amber-400 transition-colors h-auto"
      onClick={onClick}
      data-testid={`orphan-node-${node.id}`}
    >
      <div className="flex items-center gap-2">
        {node.type === 'goal' ? (
          <Target className="w-3.5 h-3.5 text-amber-500 shrink-0" aria-hidden="true" />
        ) : (
          <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 shrink-0" aria-hidden="true" />
        )}
        <span className="text-xs font-medium text-foreground truncate">{node.title}</span>
        {node.status && (
          <Badge variant={statusVariant(node.status)} className="ml-auto text-xs">
            {t(node.status)}
          </Badge>
        )}
      </div>
      {node.targetDate && (
        <p className="text-xs text-muted-foreground mt-1 pl-5">
          {t('Target')}: {formatDate(node.targetDate)}
        </p>
      )}
      {/* B6: show archived-parent hint — "↑ Goal (archived): <title> — re-anchor or archive" */}
      {archivedParentLabel && (
        <p className="text-xs text-amber-700 mt-1 pl-5">
          {archivedParentLabel} — {t('Re-anchor or archive this outcome')}
        </p>
      )}
    </Button>
  );
}

// (findNodeById and toUINode are in ./tree.utils — H4 extraction)
